import React, { useState, useEffect } from 'react';
import styles from "../../Components/Table/TableV2.module.css";
import EditIcon from "../../assets/Edit.svg";
import AddIcon from "../../assets/Add.svg";
import DeleteIcon from "../../assets/Delete.svg";
import TaskDetailsForm from '../../Components/TaskDetailsForm/TaskDetailsForm';
import TimesheetSubconForm from '../../Components/TimesheetSubconForm/TimesheetSubconForm';

import { dispatch } from '../../redux/store';
import { updateProjectDetails, deleteSubcontractorTask } from '../../redux/slices/suppliers';
import { 
    postSubcontractorTimesheets, 
    updateSubcontractorTimesheet,
    deleteSubcontractorTimesheet
} from '../../redux/slices/project';
import ConfirmationPopUp from '../../Components/ConfirmationPopUp/ConfirmationPopUp';
import { Box, CircularProgress, Tooltip } from '@mui/material';

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "TBA";
    const number = parseFloat(value);
    if (isNaN(number)) return "TBA";
    return number.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const SubcontractorTaskTable = ({
    subcontractorId,
    subcontractorName,
    rowData,
    isAdmin = false,
    isLoading = false,
}) => {
    const rowClasses = `${styles.tableRow}`;
    const [displayTaskForm, setDisplayTaskForm] = useState(false);
    const [displayTimesheetForm, setDisplayTimesheetForm] = useState(false);
    const [displayConfirmationPopUp, setDisplayConfirmationPopUp] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [currentRows, setCurrentRows] = useState(rowData);


    const [sortConfig, setSortConfig] = useState({
        key: 'status', // Default sort key
        direction: 'ascending', // Default sort direction
    });

    useEffect(() => {
        let sortedRows = [...rowData];
        // Default sort logic - prioritize "ongoing" at the top
        sortedRows.sort((a, b) => {
            const aStatus = a.status?.toLowerCase() || '';
            const bStatus = b.status?.toLowerCase() || '';
            
            const aIsOngoing = aStatus.includes('ongoing');
            const bIsOngoing = bStatus.includes('ongoing');
            const aIsFinish = aStatus.includes('finish');
            const bIsFinish = bStatus.includes('finish');

            // Ongoing statuses first
            if (aIsOngoing && !bIsOngoing) return -1;
            if (!aIsOngoing && bIsOngoing) return 1;

            // Finished statuses last
            if (aIsFinish && !bIsFinish) return 1;
            if (!aIsFinish && bIsFinish) return -1;

            // If both are the same type (both ongoing, both finished, or both other), sort alphabetically
            return aStatus.localeCompare(bStatus);
        });
        setCurrentRows(sortedRows);
    }, [rowData]);



    const sortRows = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedRows = [...currentRows].sort((a, b) => {
            // Map the processed key back to the actual data property
            let aValue, bValue;
            
            // Map header keys to data properties
            const keyMapping = {
                'runner': 'runner',
                'taskoutsourced': 'taskOutsourced',
                'status': 'status',
                'subcontractorpaid': 'subcontractorPaid',
                'internalcost': 'totalInternalCost',
                'feeearnt': 'totalFeeEarnt',
                'costranking': 'costRanking',
                'timeranking': 'timeRanking',
                'qualityranking': 'qualityRanking',
                'wasthisprojectsuccessfullyoutsourced?': 'wasProjectSuccessfullyOutsourced',
                'finalcomment': 'finalComment'
            };

            const dataProperty = keyMapping[key] || key;
            aValue = a[dataProperty];
            bValue = b[dataProperty];

            // Special handling for status column to maintain ongoing first, finished last priority
            if (key === 'status') {
                const aStatus = a.status?.toLowerCase() || '';
                const bStatus = b.status?.toLowerCase() || '';
                
                const aIsOngoing = aStatus.includes('ongoing');
                const bIsOngoing = bStatus.includes('ongoing');
                const aIsFinish = aStatus.includes('finish');
                const bIsFinish = bStatus.includes('finish');

                // Always prioritize ongoing first when sorting status
                if (aIsOngoing && !bIsOngoing) return -1;
                if (!aIsOngoing && bIsOngoing) return 1;

                // Always put finished last when sorting status
                if (aIsFinish && !bIsFinish) return 1;
                if (!aIsFinish && bIsFinish) return -1;

                // If both are the same type, sort according to direction
                if (direction === 'ascending') {
                    return aStatus.localeCompare(bStatus);
                } else {
                    return bStatus.localeCompare(aStatus);
                }
            }

            // Handle boolean values
            if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                if (direction === 'ascending') {
                    return aValue === bValue ? 0 : aValue ? 1 : -1;
                } else {
                    return aValue === bValue ? 0 : aValue ? -1 : 1;
                }
            }

            // Handle numeric values (for rankings and currency)
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'ascending' ? aValue - bValue : bValue - aValue;
            }

            // Handle string values
            const aStr = String(aValue || '');
            const bStr = String(bValue || '');
            
            if (direction === 'ascending') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });

        setSortConfig({ key, direction });
        setCurrentRows(sortedRows);
    };

    const openTaskForm = (display, rowData) => {
        setSelectedRow(rowData);
        setDisplayTaskForm(display);
    }

    const openTimesheetForm = (display, rowData) => {
        setSelectedRow(rowData);
        setDisplayTimesheetForm(display);
    }

    const openConfirmationPopUp = (display, rowData) => {
        setSelectedRow(rowData);
        setDisplayConfirmationPopUp(display);
    }

    const handleDelete = (taskID) => {
        dispatch(deleteSubcontractorTask(taskID, subcontractorId));
        setDisplayConfirmationPopUp(false);
    };

    const handleTaskFormSubmit = async (formData, subcontractorId) => {
        console.log(formData);
        dispatch(updateProjectDetails(formData, subcontractorId));
    }

    const handleTimesheetFormSubmit = async (formData, operation = 'create', entryId = null) => {
        console.log('Timesheet operation:', operation, formData);
        
        switch (operation) {
            case 'create':
                dispatch(postSubcontractorTimesheets(formData, subcontractorId));
                break;
            case 'update':
                if (entryId) {
                    dispatch(updateSubcontractorTimesheet(entryId, formData, subcontractorId));
                }
                break;
            case 'delete':
                if (entryId) {
                    dispatch(deleteSubcontractorTimesheet(entryId, subcontractorId));
                }
                break;
            default:
                console.error('Unknown timesheet operation:', operation);
        }
    }

    return (
        <React.Fragment>
            <div
                className={styles.tableContainer}
                style={{

                    overflowY: "auto",
                    overflowX: "auto", // Allow horizontal scrolling
                    background: "inherit",
                    marginBottom: "20px"
                }}
            >
                <table className={styles.table} style={{ width: 'max-content' }}>
                    <thead>
                        <tr className={styles.tableHeader}>
                            {isAdmin && (
                                <React.Fragment>
                                    <th className={styles.th} style={{ width: "3%", textAlign: "center" }}>
                                        <div className={styles.CenterContent}>Delete</div>
                                    </th>
                                    <th className={styles.th} style={{ width: "3%", textAlign: "center" }}>
                                        <div className={styles.CenterContent}>
                                            Edit
                                        </div>
                                    </th>
                                    <th className={styles.th} style={{ width: "3%", textAlign: "center" }}>
                                        <div className={styles.CenterContent}>
                                            Timesheets
                                        </div>
                                    </th>
                                </React.Fragment>
                            )}
                            <th key={0} className={styles.th}>
                                Project Address
                            </th>
                            {[
                                "Runner",
                                "Task Outsourced",
                                "Status",
                                "Subcontractor Paid",
                                "Internal Cost",
                                "Fee Earnt",
                                "Cost Ranking",
                                "Time Ranking",
                                "Quality Ranking",
                                "Was this Project successfully outsourced?",
                                "Final Comment"
                            ].map((header, index) => {
                                const headerKey = header.toLowerCase().replace(/\s+/g, ''); // Create a key for sorting
                                return (
                                    <th
                                        key={index + 1}
                                        className={styles.th}
                                        style={{ textAlign: header === "Final Comment" ? "left" : "center", cursor: 'pointer' }}
                                        onClick={() => sortRows(headerKey)} // Add onClick for sorting
                                    >
                                        {header}
                                        {sortConfig.key === headerKey && (
                                            sortConfig.direction === 'ascending' ? ' ▲' : ' ▼'
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className={styles.tbody}>
                        {isLoading ? (
                            <tr>
                                <td
                                    colSpan={isAdmin ? 12 : 9}
                                    style={{
                                        textAlign: "center",
                                        padding: "20px",
                                        color: "#888",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                      <CircularProgress size={24} />
                                    </Box>
                                </td>
                            </tr>
                        ) : rowData?.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={isAdmin ? 12 : 9}
                                    style={{
                                        textAlign: "center",
                                        color: "#4a60a8",
                                        fontWeight: "bold",
                                    }}
                                >
                                    No data available.
                                </td>
                            </tr>
                        ) : (
                            currentRows?.map((row, rowIndex) => {
                                const values = [
                                    row.runner,
                                    row.taskOutsourced,
                                    row.status,
                                    formatCurrency(row.subcontractorPaid),
                                    formatCurrency(row.totalInternalCost),
                                    formatCurrency(row.totalFeeEarnt),
                                    row.costRanking ? row.costRanking.charAt(0) : "TBA",
                                    row.timeRanking ? row.timeRanking.charAt(0) : "TBA",
                                    row.qualityRanking ? row.qualityRanking.charAt(0) : "TBA",
                                    row.wasProjectSuccessfullyOutsourced,
                                    row.finalComment,
                                ];

                                const statusIndex = 2;
                                const costRankingIndex = 6;
                                const timeRankingIndex = 7;
                                const qualityRankingIndex = 8;
                                const finalCommentIndex = 10;

                                // Check if status includes "finish" (case-insensitive)
                                const isStatusFinished = row.status?.toLowerCase().includes('finish');

                                return (
                                    <tr key={rowIndex} className={rowClasses} style={{ background: "white" }}>
                                        {isAdmin && (
                                            <React.Fragment>
                                                <td className={styles.td} style={{ textAlign: "center" }}>
                                                    <div className={styles.CenterContent}>
                                                        <img
                                                            src={DeleteIcon}
                                                            alt="Delete"
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => openConfirmationPopUp(true, row)}
                                                        />
                                                    </div>
                                                </td>
                                                <td className={styles.td} style={{ textAlign: "center" }}>
                                                    <div className={styles.CenterContent}>
                                                        <img
                                                            src={EditIcon}
                                                            alt="Edit"
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => openTaskForm(true, row)}
                                                        />
                                                    </div>
                                                </td>
                                                <td className={styles.td} style={{ textAlign: "center" }}>
                                                    <div className={styles.CenterContent}>
                                                        <img
                                                            src={AddIcon}
                                                            alt="Add"
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => openTimesheetForm(true, row)}
                                                        />
                                                    </div>
                                                </td>
                                            </React.Fragment>
                                        )}
                                        <td key={0} className={styles.td}>
                                            {row.projectAddress}
                                        </td>
                                                                {values.map((cell, index) => {
                            const isRankingColumn = [costRankingIndex, timeRankingIndex, qualityRankingIndex].includes(index);
                            const cellStyle = {
                                textAlign: index === finalCommentIndex ? "left" : "center",
                                color: isRankingColumn && !isStatusFinished ? '#aaa' : 'inherit',
                            };

                            return (
                                <td
                                    key={index + 1}
                                    className={styles.td}
                                    style={cellStyle}
                                >
                                    {index >= statusIndex && (cell === "" || cell === null) ? "TBA" : cell}
                                </td>
                            );
                        })}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {displayTimesheetForm &&
                <TimesheetSubconForm
                    projectNumber={selectedRow?.projectNumber}
                    subcontractorName={subcontractorName}
                    projectAddress={selectedRow?.projectAddress}
                    onSubmit={(values, operation, entryId) => handleTimesheetFormSubmit(values, operation, entryId)}
                    onClose={() => setDisplayTimesheetForm(false)}
                    editMode={selectedRow?.editMode || false}
                    existingEntry={selectedRow?.timesheetEntry || null}
                />
            }

            {displayConfirmationPopUp &&
                <ConfirmationPopUp
                    isVisible={displayConfirmationPopUp}
                    onConfirm={() => handleDelete(selectedRow?.ID)}
                    onClose={() => setDisplayConfirmationPopUp(false)}
                />
            }

            {displayTaskForm &&
                <TaskDetailsForm
                    projectData={selectedRow}
                    onSubmit={(values) => handleTaskFormSubmit(values, subcontractorId)}
                    onClose={() => setDisplayTaskForm(false)}
                />
            }


        </React.Fragment>
    );
};

export default SubcontractorTaskTable;
